import { useEffect } from "react";
import { useEditor } from "../store.js";

function isEditingInInput(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  return target.isContentEditable;
}

/**
 * Global keyboard shortcuts for the editor.
 *  - Delete / Backspace: remove selected widget / column / section
 *  - Ctrl/Cmd + Z: undo
 *  - Ctrl/Cmd + Shift + Z, or Ctrl + Y: redo
 *  - Ctrl/Cmd + C: copy selected widget to in-memory clipboard
 *  - Ctrl/Cmd + V: paste clipboard widget after selection
 *  - Ctrl/Cmd + D: duplicate selected widget in place
 *  - Esc: clear selection
 *
 * All shortcuts are no-ops when the user is typing in a form field.
 */
export function useKeyboardShortcuts() {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      const inInput = isEditingInInput(e.target);
      const state = useEditor.getState();

      // Undo / redo
      if (meta && e.key.toLowerCase() === "z" && !e.shiftKey) {
        e.preventDefault();
        useEditor.temporal.getState().undo();
        return;
      }
      if (
        (meta && e.key.toLowerCase() === "z" && e.shiftKey) ||
        (meta && e.key.toLowerCase() === "y")
      ) {
        e.preventDefault();
        useEditor.temporal.getState().redo();
        return;
      }

      // Clipboard & duplicate — widget-level operations.
      if (!inInput && meta && e.key.toLowerCase() === "c") {
        if (state.selection?.kind === "widget") {
          e.preventDefault();
          state.copyWidget();
        }
        return;
      }
      if (!inInput && meta && e.key.toLowerCase() === "v") {
        if (state.clipboardWidget) {
          e.preventDefault();
          state.pasteWidget();
        }
        return;
      }
      if (!inInput && meta && e.key.toLowerCase() === "d") {
        if (state.selection?.kind === "widget") {
          // preventDefault is critical — Ctrl+D in browsers adds a
          // bookmark, which we definitely do not want here.
          e.preventDefault();
          state.duplicateWidget(state.selection.id);
        }
        return;
      }

      // Delete selected entity (widget / column / section).
      if (e.key === "Delete" || e.key === "Backspace") {
        if (inInput) return;
        if (state.selection) {
          e.preventDefault();
          state.deleteSelected();
        }
        return;
      }

      // Esc: clear selection
      if (e.key === "Escape") {
        if (inInput) return;
        state.clearSelection();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
}
